// Shared helpers for the customer autocomplete pickers (reservation booker & co-travelers):
// the input display formatter and the debounced server-side search pipeline.
import { AbstractControl } from '@angular/forms';
import { CustomerSearchItem } from '@models/customers';
import { debounceTime, distinctUntilChanged, map, Observable, of, switchMap } from 'rxjs';

// The minimal shape stored as a picker's picked value: just enough to identify the customer and
// render the input. Deliberately separate from the rich CustomerSearchItem dropdown-option shape —
// on rehydration we only have a joined name, not the individual option fields.
export interface PickedCustomer {
  id: number;
  label: string;
}

// Minimum characters typed before a server-side customer search fires.
export const MIN_SEARCH_LENGTH = 3;

// Joins a search-result's name parts into the label shown once it's picked.
export function customerLabel(item: Pick<CustomerSearchItem, 'firstName' | 'lastName'>): string {
  return [item.firstName, item.lastName].filter(Boolean).join(' ');
}

// Maps a rich dropdown option to the minimal picked value stored on the control.
export function pickCustomer(item: CustomerSearchItem): PickedCustomer {
  return { id: item.id, label: customerLabel(item) };
}

// Renders the picked customer (or raw string while typing) in the input.
// Shared by the booker and co-traveler autocompletes; called with null for empty rows.
export function displayCustomer(customer: PickedCustomer | string | null): string {
  if (customer == null) return '';
  return typeof customer === 'string' ? customer : customer.label;
}

// Debounced server-side customer search driven by a control's value changes. While typing the
// value is a string; once an option is picked it's an object (the search is skipped). The caller
// is responsible for subscription teardown (e.g. takeUntilDestroyed).
export function customerSearch(
  control: AbstractControl,
  search: (term: string) => Observable<CustomerSearchItem[]>,
): Observable<CustomerSearchItem[]> {
  return control.valueChanges.pipe(
    debounceTime(300),
    map((value) => (typeof value === 'string' ? value.trim() : value)),
    distinctUntilChanged(),
    switchMap((value) =>
      typeof value === 'string' && value.length >= MIN_SEARCH_LENGTH ? search(value) : of([] as CustomerSearchItem[]),
    ),
  );
}
